<template>
    <v-col class="pb-0">
        <template v-if="companyAccountsWithAppActivated.length && classifiers_loaded">
            <v-row no-gutters>
                <v-col class="mb-2">
                    <ModernFilterBar
                        :filter-controller="filterController"
                    >
                        <template v-slot:right="{filters, filterAdd}">
                            <AddFilterButton
                                :filters="filters"
                                @add_filter="filterAdd($event)"
                            />
                            <v-tooltip bottom>
                                <template v-slot:activator="{ on }">
                                    <icon-button icon="Delete" :on="on" @click="onResetFilters" />
                                </template>
                                {{ $t("Clear filters") }}
                            </v-tooltip>
                            <v-tooltip bottom>
                                <template v-slot:activator="{ on }">
                                    <icon-button
                                        icon="Save"
                                        :disabled="$route.query && Object.keys($route.query).length < 1"
                                        :on="on"
                                        @click="onSaveFiltersClick"
                                    />
                                </template>
                                {{ $t("Save filters") }}
                            </v-tooltip>
                        </template>
                    </ModernFilterBar>
                </v-col>
            </v-row>
            <v-row v-if="tabs.length > 0" no-gutters>
                <v-col>
                    <v-toolbar flat class="list-tabs-toolbar pa-0" color="transparent">
                        <v-toolbar-title>
                            <v-tabs
                                v-model="tab_index"
                                background-color="transparent"
                                class="denser-tabs"
                                slider-size="4"
                            >
                                <v-tab
                                    v-for="(tab, tabindex) in tabs"
                                    :key="'tidx' + tabindex"
                                    @change="changeTab(tab, tabindex)"
                                >
                                    <span class="caption font-weight-medium">
                                        {{ tab.text }}
                                    </span>
                                </v-tab>
                            </v-tabs>
                        </v-toolbar-title>
                        <v-spacer />
                        <v-menu offset-y>
                            <template v-slot:activator="{ on }">
                                <v-btn color="primary" text v-on="on">
                                    <v-icon size="24" left>mdi-plus-box</v-icon>
                                    {{ $t("Add supplier") }}
                                </v-btn>
                            </template>
                            <v-card>
                                <v-card-text class="py-0 px-0">
                                    <v-list dense>
                                        <v-list-item v-for="tab in tabs" :key="tab.id" @click="addSupplierClick(tab.id)">
                                            <v-list-item-content class="py-2">
                                                <v-list-item-title class="font-weight-regular subtitle-2">
                                                    {{ tab.text }}
                                                </v-list-item-title>
                                            </v-list-item-content>
                                        </v-list-item>
                                    </v-list>
                                </v-card-text>
                            </v-card>
                        </v-menu>
                        <ColumnController
                            :headers="richDataTableController.getAllHeaders()"
                            :visible-headers="richDataTableController.getVisibleHeaders().map(x => x.value)"
                            @update_columns="richDataTableController.setVisibleHeadersWithoutLoadData($event)"
                        />
                    </v-toolbar>
                </v-col>
            </v-row>
            <v-row v-if="tabs.length > 0 && !hideTable" no-gutters>
                <v-col>
                    <RichDataTable :rich-data-table-controller="richDataTableController">
                        <template v-slot:item.edit="{ item }">
                            <icon-button @click.stop="editSupplierClick(item.id)">
                                edit
                            </icon-button>
                        </template>
                        <template v-slot:item.name="{ item }">
                            <router-link :to="`suppliers/${item.id}`">
                                {{ item.name }}
                            </router-link>
                        </template>
                        <template v-slot:item.parent_id="{ item }">
                            <router-link :to="`suppliers/${item.parent_id}`">
                                {{ item.parent_id_disp }}
                            </router-link>
                        </template>
                        <template v-slot:default="{ item, header }">
                            {{ header.format(item, header) }}
                        </template>
                    </RichDataTable>
                </v-col>
            </v-row>
            <v-row v-else-if="tabs.length === 0 && !hideTable">
                <v-col class="grey--text text--darken-2">
                    {{ $t("Supplier types are not defined. Please go to the suppliers app settings to add.") }}
                </v-col>
            </v-row>
        </template>
    </v-col>
</template>

<script>
import AccountModule from "@/store/modules/account";
import FilterBarController from "../../components/ModernFilterBar/FilterBarController";
import ModernFilterBar from "../../components/ModernFilterBar";
import ColumnController from "../../components/ColumnController";
import {computed, onMounted, reactive, ref, watch} from "@vue/composition-api";
import { useTable } from "@/composables/use-table";
import AppModule from "../../store/modules/app";
import SupplierModule from "../../store/modules/supplier";
import { getFieldFormatByType } from "../../composables/use-custom-fields";
import AddFilterButton from "../../components/NewFilterBar/components/AddFilterButton";
import UrlAdapter from "@/urlAdapter/urlAdapter";
import RichDataTableController from "../../components/RichDataTable/RichDataTableController";
import RichDataTable from "../../components/RichDataTable/RichDataTable"
import errorDialog from "@/views/ErrorDialog/errorDialog"
import DialogModule from "../../store/modules/dialog";
import BookmarkModule from "@/store/modules/bookmark";

export default {
    name: "SuppliersList",
    components: {
        ModernFilterBar,
        ColumnController,
        AddFilterButton,
        RichDataTable
    },
    setup(props, ctx) {
        const { fetchClassifiers, supplier_types, custom_fields } = AppModule();
        const { available_company_accounts, getDefaultCompanyAccount } = AccountModule();
        const bookmarkModule = BookmarkModule()
        const { fetchSuppliers } = SupplierModule();
        const { setDialogOptions, openDialog } = DialogModule();
        const { table_footer_props, formatTableHeaderClass } = useTable();

        const urlAdapter = new UrlAdapter()
        urlAdapter.loadDefaultBookmark('supplier', bookmarkModule.bookmarks.value)
        /** @type {FilterBarController} */
        const filterController = reactive(FilterBarController.init(urlAdapter))
        /** @type {RichDataTableController} */
        const richDataTableController = reactive(RichDataTableController.initAsync(urlAdapter))
        const options = [
            { key: "sortBy", default_value: "name" },
            { key: "sortDesc", default_value: false },
            { key: "itemsPerPage", default_value: 50 },
            { key: "page", default_value: 1 }
        ]
        richDataTableController.setDefaultOptions(options)

        richDataTableController.setOnRowClick((...a) => openSupplierClick(...a))

        const companyAccountsWithAppActivated = computed(() => {
            return available_company_accounts.value.filter(x => x.features.includes('supplier'))
        })

        let active_account = getDefaultCompanyAccount(companyAccountsWithAppActivated);
        const classifiers_loaded = ref(false);
        const tab_index = ref(0);
        const total_items = ref(0);
        const items = ref([]);
        const headers = ref([]);
        const hideTable = ref(false);

        const selected_account = computed(() => {
            let account_id_str = filterController.getValue('account_id');
            if (!account_id_str) return active_account;
            let account_id = parseInt(account_id_str);
            return companyAccountsWithAppActivated.value.find(x => x.id === account_id);
        });

        const getMembers = computed(() => selected_account.value ? selected_account.value.members.filter(m => m.status === "active" && (m.type === "person" || m.type === "group")) : [])

        const onSaveFiltersClick = () => {
            setDialogOptions({
                component: () => import("@/views/Dialog/BookmarkDialog"),
                payload: {
                    type: "supplier"
                }
            });
            openDialog();
        };

        const getAccountCustomFields = () => {
            const type_id = filterController.getValue('type_id')
            if (custom_fields.value && custom_fields.value[selected_account.value.id]) {
                return custom_fields.value[selected_account.value.id].filter(cf => cf.related_type === `supplier.${type_id}`)
            } else {
                return []
            }
        }

        urlAdapter.addGetStateCallback(() => ({
            type_id: urlAdapter.getUrlParameter('type_id')
        }))

        filterController.initFilters(
            [
                { key: "account_id", default_value: [active_account.id], items: companyAccountsWithAppActivated.value, default: true },
                { key: "keyword", default_value: [""], default: true },
                { key: "custom_field", default_value: [], items: getAccountCustomFields, members: getMembers.value, repeatable: true },
            ]
        )

        filterController.initCustomParameters(
            [
                { key: "columns", default_value: ["name", "edit"]},
                { key: "type_id", default_value: []},
            ]
        )
        richDataTableController.setLoadDataFunc((...a) => backendQuery(...a))
        filterController.setBeforeFilterInputFunc((...a) => beforeFilterInput(...a))

        const visible_headers = computed(() => {
            if (filterController.getValue('columns')) {
                return headers.value.filter(x => filterController.getValue('columns').includes(x.value));
            } else {
                return headers.value;
            }
        });
        const visible_columns = computed(() => {
            return visible_headers.value.map(x => x.value);
        });
        const getTabsByAccountId = (accountId) => {
            if (supplier_types.value[accountId] === undefined) return [];
            return Array.sort_by(supplier_types.value[accountId].sort(), "sort_order").map(x => {
                return {
                    id: x.id,
                    text: x.title
                };
            });
        }

        const tabs = computed(() => {
            return getTabsByAccountId(selected_account.value.id)
        });

        let lastLoaded = 0;
        const loadData = async (overrideAccount) => {
            const selectedAccountId = overrideAccount ? overrideAccount.id : selected_account.value.id

            if (lastLoaded === selectedAccountId) return;
            lastLoaded = selectedAccountId;

            let data = {
                types: ["supplier_types", "custom_fields", "countries"],
                account_id: selectedAccountId
            };
            try {
                await fetchClassifiers(data)
                let tabs = getTabsByAccountId(selectedAccountId)

                if (tabs.length > 0) {
                    let type_id_str = filterController.getValue('type_id');
                    let supplier_type_id = 0;
                    if (type_id_str.length < 1) {
                        supplier_type_id = tabs[tab_index.value].id;
                        filterController.setValueNoPersist("type_id", tabs[tab_index.value].id);
                    }
                    else {
                        supplier_type_id = parseInt(type_id_str);
                        let idx = tabs.map(x => x.id).indexOf(supplier_type_id);
                        tab_index.value = (idx < 0) ? 0 : idx;
                    }

                    await setHeaders(supplier_type_id, overrideAccount);
                }
                classifiers_loaded.value = true;
            } catch (ex) {
                errorDialog.showError(ex)
            }
        };
        const setHeaders = (supplier_type_id, overrideAccount) => {
            const selectedAccountId = overrideAccount ? overrideAccount.id : selected_account.value.id

            //todo: duplicated
            let key = "supplier.{0}".format(supplier_type_id);
            let ret = custom_fields.value[selectedAccountId]
                .filter(x => x.related_type === key && x.type !== "contacts" && !x.type.startsWith("suppliers."))
                .sort((a, b) => {
                    if (a.sort_order < b.sort_order) return -1;
                    if (a.sort_order > b.sort_order) return 1;
                    return 0;
                })
                .map(x => {
                    let col = {
                        account_id: selectedAccountId,
                        value: x.name,
                        text: x.title,
                        is_listed: x.is_listed,
                        format: getFieldFormatByType(x.type),
                    };
                    if (x._data && x._data.choices)
                        col.choices = x._data.choices;
                    if (x.type === "choice_multi" || x.type === "user_multi")
                        col.sortable = false;
                    return col;
                });
            ret.push({
                text: "",
                value: "edit",
                sortable: false,
                align: "right",
                width: 0,
                is_listed: 1,
            });
            const visible_columns = ret.filter(x => x.is_listed === 1).map(x => x.value)

            filterController.setValueNoPersist("columns", visible_columns);

            richDataTableController.setAllHeaders(ret)
            richDataTableController.setVisibleHeadersWithoutLoadData(visible_columns)
        };
        const backendQuery = async (params) => {
            if (!params) {
                params = urlAdapter.getUrlParameters()
            }

            if (params['type_id'] !== undefined && params['type_id'].length < 1) {
                const accountTabs = getTabsByAccountId(params['account_id'][0])
                if (accountTabs.length === 0) {
                    return
                }

                params['type_id'] = accountTabs[tab_index.value].id;
            }

            let account_id = filterController.getValue("account_id");
            const resp = await fetchSuppliers(account_id, params)
            hideTable.value = false;
            richDataTableController.setRows(resp.items, resp.total_items)
        };
        const addSupplierClick = (type_id) => {
            ctx.root._router.push({
                name: "supplier_new",
                params: { account_id: selected_account.value.id, type_id: type_id },
            });
        };
        const editSupplierClick = (supplier_id) => {
            ctx.root._router.push({
                name: "supplier_edit",
                params: { id: supplier_id },
            });
        };
        const openSupplierClick = (supplier) => {
            ctx.root._router.push({
                name: "supplier",
                params: { id: supplier.id },
            });
        }
        const changeTab = (tab, idx) => {
            hideTable.value = true;
            let supplier_type_id = tabs.value[idx].id;
            filterController.setValue("type_id", supplier_type_id);
            setHeaders(supplier_type_id);
        };

        const beforeFilterInput = async function(filter_key, value){
            if (['account_id'].includes(filter_key) && value.length) {
                await filterController.setValueNoPersist('type_id', '')
                await loadData(value[0]);
            }
        }

        const onResetFilters = async () => {
            await filterController.resetFilters()
            await loadData()
        }

        onMounted(async () => {
            filterController.startUrlWatcherWithLoadData();
            await loadData()
            await filterController.persistCurrentFilterStateToUrl()
        })

        return {
            available_company_accounts,
            headers,
            items,
            total_items,
            selected_account,
            table_footer_props,
            visible_headers,
            visible_columns,
            tab_index,
            tabs,
            classifiers_loaded,
            formatTableHeaderClass,
            hideTable,
            addSupplierClick,
            editSupplierClick,
            openSupplierClick,
            changeTab,
            filterController,
            /** @type {RichDataTableController} */
            richDataTableController,
            onSaveFiltersClick,
            supplier_types,
            onResetFilters,
            companyAccountsWithAppActivated
        };
    }
};
</script>
